import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { getAuthUser } from '../../services/authApi';

interface HeaderProps {
  onMenuClick: () => void;
  isCollapsed?: boolean;
  pageTitle?: string;
  pageSubtitle?: string;
}

function formatShortDate() {
  return new Date().toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

const Header: React.FC<HeaderProps> = ({ onMenuClick, isCollapsed, pageTitle, pageSubtitle }) => {
  const [userName, setUserName] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    // Prefer the real name the API returned; fall back to the email prefix
    // for sessions created by the older NowCerts SSO flow.
    const user = getAuthUser();
    if (user?.fullName) {
      setUserName(user.fullName);
      return;
    }
    const email = sessionStorage.getItem('userEmail');
    if (email) {
      const namePart = email.split('@')[0];
      const formatted = namePart
        .split(/[._-]/)
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ');
      setUserName(formatted);
    }
  }, []);

  return (
    <motion.header
      className={`header ${isCollapsed ? 'header--collapsed' : ''}`}
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
    >
      {/* ── Left: menu toggle + page title ── */}
      <div className="header__left">
        <button className="sidebar__toggle" onClick={onMenuClick} aria-label="Toggle menu">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>

        {pageTitle && (
          <div className="header__page-info">
            <h1 className="header__page-title">{pageTitle}</h1>
            {pageSubtitle && <p className="header__page-subtitle">{pageSubtitle}</p>}
          </div>
        )}
      </div>

      {/* ── Center: global search ── */}
      <div className={`header__search-wrap${searchFocused ? ' header__search-wrap--focused' : ''}`}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          placeholder="Search submissions, clients..."
          className="header__search-input"
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
        />
        <kbd className="header__search-kbd">⌘K</kbd>
      </div>

      {/* ── Right: date + notifications + user ── */}
      <div className="header__right">

        {/* Date chip */}
        <span className="header__date-chip">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          {formatShortDate()}
        </span>

        {/* Notification bell */}
        <motion.button
          className="header__icon-btn"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.92 }}
          aria-label="Notifications"
        >
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          <span className="header__notification-badge">3</span>
        </motion.button>

        {/* Divider */}
        <div className="header__divider" />

        {/* User chip */}
        <motion.div className="header__user" whileHover={{ scale: 1.01 }}>
          <div className="header__avatar-ring">
            <div className="header__avatar">
              <span>{localStorage.getItem('AvatarName')}</span>
            </div>
          </div>
          <div className="header__user-info">
            <span className="header__user-name">{userName || ''}</span>
          </div>
        </motion.div>

      </div>
    </motion.header>
  );
};

export default Header;
